// MergePromptModal — used by the batch bar on Items.jsx to combine N selected
// bugs into a single "build brief" via Gemini 3 Flash. Result is persisted
// to `dev_merged_prompts` (viewable later) and copied/exported from here.

import { useState } from "react";
import DOMPurify from "dompurify";
import { marked } from "marked";
import api from "../../lib/api";
import { X, Copy, Sparkles, Download, RefreshCw, Loader2 } from "lucide-react";

export default function MergePromptModal({ open, itemIds, onApplied, onClose }) {
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState("");
  const [title, setTitle]   = useState("");
  const [result, setResult] = useState(null); // {id, prompt, suggestions, ...}
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);

  if (!open) return null;

  const run = async () => {
    setBusy(true); setError(""); setApplied(false);
    try {
      const { data } = await api.post("/dev/items/merge", {
        item_ids: itemIds,
        title: title.trim() || undefined,
      });
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Merge failed");
    } finally { setBusy(false); }
  };

  const MERGE_SUGG = ["function", "effort", "impact", "roi", "phase"];
  const suggestions = result?.suggestions || {};
  const suggKeys = MERGE_SUGG.filter((k) => suggestions[k] !== undefined && suggestions[k] !== null && suggestions[k] !== "");

  const applyToSelected = async () => {
    if (!suggKeys.length) return;
    setApplying(true); setError("");
    const patch = {};
    suggKeys.forEach((k) => {
      if (k === "impact") patch.impact = Number(suggestions[k]);
      else if (k === "roi") patch.roi = Number(suggestions[k]);
      else patch[k] = suggestions[k];
    });
    try {
      await api.patch("/dev/items/batch", { item_ids: itemIds, patch });
      setApplied(true);
      onApplied && onApplied();
    } catch (e) {
      setError(e.response?.data?.detail || "Apply failed (admin only)");
    } finally { setApplying(false); }
  };

  const copyText = () => result?.prompt && navigator.clipboard.writeText(result.prompt);
  const downloadMd = () => {
    if (!result?.prompt) return;
    const blob = new Blob([result.prompt], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(result.title || "merge").replace(/[^a-z0-9]+/gi, "-")}.md`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const html = result?.prompt ? marked.parse(result.prompt, { breaks: true }) : "";

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm grid place-items-center p-6" onClick={onClose} data-testid="merge-modal">
      <div
        className="w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col rounded-xl border border-fuchsia-400/30 bg-slate-950 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-fuchsia-300" />
            <h2 className="font-display text-lg font-bold">Merge → build brief</h2>
            <span className="text-[11px] font-mono text-slate-500">
              {itemIds.length} item{itemIds.length === 1 ? "" : "s"}
            </span>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white" data-testid="merge-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {!result ? (
            <>
              <p className="text-sm text-slate-300 mb-3">
                Ships every selected item (with description, comments &amp; voice transcripts) to Gemini 3 Flash.
                Returns a single build brief you can hand a developer or paste into Claude.
              </p>
              <label className="text-xs uppercase tracking-wider text-slate-400 block mb-1">Optional title</label>
              <input
                type="text"
                placeholder="e.g. Wave 24 · Quote builder polish"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="input-dark w-full text-sm"
                data-testid="merge-title"
              />
              {error && (
                <div className="mt-4 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="merge-error">
                  {error}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="text-[10px] font-mono text-slate-500 mb-3">
                {result.model} · saved {new Date(result.generated_at).toLocaleString()}
                {result.generated_by_name ? ` · by ${result.generated_by_name}` : ""}
              </div>
              <div
                className="rich-content"
                data-testid="merge-body"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
              />
              {suggKeys.length > 0 && (
                <div className="mt-4 p-3 rounded-lg border border-fuchsia-400/20 bg-fuchsia-500/[0.04]" data-testid="merge-suggestions">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-mono uppercase tracking-widest text-fuchsia-200">Suggested fields for the merged effort</span>
                    <button type="button" onClick={applyToSelected} disabled={applying || applied}
                      className="text-[11px] font-semibold px-2 py-1 rounded border border-fuchsia-400/40 bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-100 disabled:opacity-50"
                      data-testid="merge-apply-all">
                      {applied ? "Applied ✓" : applying ? "Applying…" : `Apply to ${itemIds.length} selected`}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {suggKeys.map((k) => (
                      <span key={k} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] border border-white/15 bg-white/[0.03] text-slate-200" data-testid={`merge-suggest-${k}`}>
                        <span className="text-slate-400 uppercase tracking-wide text-[9.5px]">{k}:</span>
                        <span className="font-semibold">{String(suggestions[k])}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-white/5 flex items-center justify-end gap-2">
          {result && (
            <>
              <button type="button" onClick={copyText} className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-xs border border-white/10 hover:bg-white/5 text-slate-200" data-testid="merge-copy">
                <Copy className="w-3.5 h-3.5" /> Copy markdown
              </button>
              <button type="button" onClick={downloadMd} className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-xs border border-emerald-400/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200" data-testid="merge-download">
                <Download className="w-3.5 h-3.5" /> Download .md
              </button>
            </>
          )}
          <button
            type="button"
            onClick={run}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-semibold border border-fuchsia-400/40 bg-fuchsia-500/15 hover:bg-fuchsia-500/25 text-fuchsia-100 disabled:opacity-40"
            data-testid="merge-run"
          >
            {busy
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…</>
              : (result
                  ? <><RefreshCw className="w-3.5 h-3.5" /> Regenerate</>
                  : <><Sparkles className="w-3.5 h-3.5" /> Generate build brief</>)}
          </button>
        </div>
      </div>
    </div>
  );
}
